import type { Access } from "./access.js";
// Roles, pages, columns and actions carried over from FALCON PLM v2.0.41.
// This file is shared by the server (which enforces it) and the screens
// (which only use it to decide what to show). The server is the authority.

export const ROLES = ["Admin", "Merchandiser", "Supplier", "Agent", "QC inspector"] as const;
export type Role = (typeof ROLES)[number];

export const ACCESS_LEVELS = ["none", "view", "edit", "full"] as const;
export type AccessLevel = (typeof ACCESS_LEVELS)[number];

export const DATA_SCOPES = ["all", "supplier", "agent", "selectedPOs"] as const;
export type DataScopeType = (typeof DATA_SCOPES)[number];

export const PAGE_DEFS: ReadonlyArray<readonly [string, string]> = [
  ["dashboard", "Dashboard"], ["tasks", "My Tasks"], ["rd", "R&D"], ["products", "Garments"], ["fabric", "Fabrics"], ["techpacks", "Tech Packs"],
  ["po", "Purchase Orders"], ["sampling", "Sampling"], ["dispatch", "Sample dispatch"], ["critical", "Time & Action Plan"], ["qc", "QC / AQL"],
  ["compliance", "Compliance & Certification"], ["shipmentcontrol", "Shipment Control"], ["landedcosting", "Landed Costing"], ["supplierperformance", "Supplier Performance"],
  ["settings", "Settings"],
  ["database", "Garment Database"], ["fabricdb", "Fabric Database"], ["supplierdb", "Supplier Database"], ["agentdb", "Agent Database"],
  ["operatordb", "User Database"], ["activity", "Activity Log"],
];

export const COLUMN_DEFS: Record<string, ReadonlyArray<readonly [string, string]>> = {
  critical: [
    ["poNumber", "PO#"], ["agent", "Agent"], ["supplier", "Supplier"], ["orderDate", "Order Date"],
    ["productCode", "Product Code"], ["productName", "Product Name"], ["colour", "Colour"], ["totalQty", "Total Qty"],
    ["costUsd", "Cost USD"], ["amountUsd", "Amount USD"], ["sample", "Sample"], ["fitSample", "Fit Sample"],
    ["pps", "PPS"], ["goldSeal", "Gold seal"], ["bulkProduction", "Bulk Production"], ["qc", "QC"], ["qc2", "2nd QC"], ["readyToShip", "Ready to Ship"],
    ["etc", "ETC"], ["etd", "ETD"], ["eta", "ETA"], ["shipmentStatus", "Shipment Status"], ["received", "Received"],
    ["status", "Status"], ["activityLog", "Activity Log"],
  ],
  shipmentcontrol: [
    ["poNumber", "PO#"], ["shipment", "Shipment"], ["items", "Items"], ["productCodes", "Product Code(s)"],
    ["productNames", "Product Name(s)"], ["type", "Type"], ["container", "Container #"], ["bol", "BOL #"],
    ["carrier", "Vessel / Airline"], ["loadingPort", "Loading Port"], ["dischargePort", "Discharge Port"],
    ["etd", "ETD"], ["eta", "ETA"], ["totalQty", "Total Qty"], ["status", "Status"], ["currentStop", "Current Stop"],
    ["bolDoc", "BOL"], ["edit", "Edit"],
  ],
  po: [
    ["poNumber", "PO Number"], ["agent", "Agent"], ["supplier", "Supplier"], ["orderDate", "Order Date"], ["etc", "ETC"],
    ["etd", "ETD"], ["raisedBy", "Raised By"], ["totalQty", "Total Qty"], ["totalAmount", "Total Amount"], ["actions", "Actions"],
  ],
  qc: [
    ["poNumber", "PO#"], ["supplier", "Supplier"], ["productCode", "Product Code"], ["productName", "Product Name"],
    ["colour", "Colour"], ["photos", "Photos"], ["feedback", "Feedback"], ["aqlReport", "AQL Report"],
    ["qc2Photos", "2nd QC Photos"], ["qc2Feedback", "2nd Feedback"], ["qc2AqlReport", "2nd AQL Report"],
  ],
  fabric: [["composition", "Fibre composition (confidential)"]],
  products: [
    ["brand", "Brand"], ["productCode", "Product Code"], ["productName", "Product Name"], ["speciality", "Speciality"],
    ["category", "Category"], ["fabricCode", "Fabric Code"], ["standard", "Standard"], ["colours", "Colours"],
    ["sizes", "Sizes"], ["packing", "Packing"], ["datasheet", "Datasheet"], ["cost", "Cost & Costing (all items)"], ["actions", "Actions"],
  ],
};

export const ACTION_DEFS: ReadonlyArray<readonly [string, string]> = [
  ["assignTasks", "Assign / manage tasks"], ["updateTasks", "Update assigned tasks"],
  ["manageRD", "Create / edit R&D developments"], ["convertRD", "Approve and convert R&D developments"],
  ["manageCompliance", "Create / edit compliance records"],
  ["manageSamples", "Create / review sample rounds"],
  ["dispatchSamples", "Enter sample dispatch details (sent, received, forwarded)"],
  ["approveArtwork", "Approve artwork, labels & packaging"],
  ["manageLandedCost", "Create / edit landed costing"],
  ["managePackingList", "Create / edit / submit packing lists"],
  ["editWorkflow", "Edit Time & Action Plan workflow"], ["approveSamples", "Update Sample / Fit Sample"],
  ["approvePPS", "Update PPS"], ["approveSTP", "Approve STP (Straight To Production)"], ["updateBulk", "Update Bulk Production"], ["performQC", "Perform / update QC"],
  ["createShipment", "Create shipments"], ["updateShipment", "Edit shipment details / status"], ["markReceived", "Mark items Received"],
  ["createPO", "Create Purchase Orders"], ["editPO", "Edit Purchase Orders"], ["uploadDocuments", "Upload documents / photos"],
  ["printPdf", "Print / PDF / download"], ["manageDatabase", "Manage database setup"], ["deleteRecords", "Delete records"],
  ["viewActivity", "View Activity Log"], ["manageUsers", "Manage users"], ["managePermissions", "Manage user permissions"],
];

export interface ColumnPermission { view: boolean; edit: boolean }
export interface DataScope { type: DataScopeType; supplierUid: string; agentUid: string; poUids: string[] }
export interface Permissions {
  pages: Record<string, AccessLevel>;
  columns: Record<string, Record<string, ColumnPermission>>;
  actions: Record<string, boolean>;
  dataScope: DataScope;
}

export function isRole(value: unknown): value is Role {
  return typeof value === "string" && (ROLES as readonly string[]).includes(value);
}

function blankColumns(): Permissions["columns"] {
  const out: Permissions["columns"] = {};
  for (const [page, defs] of Object.entries(COLUMN_DEFS)) {
    out[page] = {};
    for (const [key] of defs) out[page][key] = { view: true, edit: true };
  }
  return out;
}

/** The default permissions for a role, identical to v2.0.41. */
export function roleTemplate(role: Role): Permissions {
  const p: Permissions = {
    pages: Object.fromEntries(PAGE_DEFS.map(([id]) => [id, "none" as AccessLevel])),
    columns: blankColumns(),
    actions: Object.fromEntries(ACTION_DEFS.map(([id]) => [id, false])),
    dataScope: { type: "all", supplierUid: "", agentUid: "", poUids: [] },
  };
  const setPages = (ids: string[], level: AccessLevel) => ids.forEach((id) => (p.pages[id] = level));
  const setActions = (ids: string[]) => ids.forEach((id) => (p.actions[id] = true));
  const hideCosts = () => {
    for (const k of ["costUsd", "amountUsd"]) if (p.columns.critical[k]) p.columns.critical[k].view = false;
    if (p.columns.po.totalAmount) p.columns.po.totalAmount.view = false;
    if (p.columns.products.cost) p.columns.products.cost.view = false;
    Object.values(p.columns.critical).forEach((x) => (x.edit = false));
  };

  if (role === "Admin") {
    PAGE_DEFS.forEach(([id]) => (p.pages[id] = "full"));
    ACTION_DEFS.forEach(([id]) => (p.actions[id] = true));
    return p;
  }
  // Fibre composition is confidential (v2.0.41): only Admins see it unless an Admin allows it.
  p.columns.fabric.composition = { view: false, edit: false };
  if (role === "Merchandiser") {
    setPages(["dashboard"], "view");
    setPages(["tasks", "rd", "compliance", "critical", "shipmentcontrol", "products", "techpacks", "sampling", "po", "qc"], "edit");
    setPages(["supplierperformance"], "view");
    setPages(["landedcosting"], "edit");
    setPages(["fabric", "settings", "database", "fabricdb", "supplierdb", "agentdb"], "view");
    setActions(["approveArtwork", "assignTasks", "updateTasks", "manageRD", "manageCompliance", "manageSamples", "manageLandedCost", "managePackingList", "editWorkflow", "approveSamples", "approvePPS", "updateBulk", "performQC",
      "createShipment", "updateShipment", "markReceived", "createPO", "editPO", "uploadDocuments", "printPdf"]);
    return p;
  }
  if (role === "Supplier") {
    setPages(["dashboard", "compliance", "supplierperformance", "shipmentcontrol", "po", "qc"], "view");
    setPages(["tasks", "critical", "sampling", "dispatch"], "edit");
    setActions(["updateTasks", "uploadDocuments", "managePackingList", "dispatchSamples"]);
    p.dataScope.type = "supplier";
    p.columns.critical.agent.view = false;
    hideCosts();
    return p;
  }
  // QC inspector: no pages at all. They see only Ready for QC, their own inspections and Help — the server refuses
  // everything else (see QC_INSPECTOR_API in server/src/app.ts), whatever the stored permissions say.
  if (role === "QC inspector") {
    p.dataScope.type = "all";
    hideCosts();
    return p;
  }
  // Agent
  setPages(["dashboard", "supplierperformance", "shipmentcontrol", "po"], "view");
  setPages(["tasks", "critical", "dispatch"], "edit");
  setActions(["updateTasks", "dispatchSamples"]);
  p.dataScope.type = "agent";
  hideCosts();
  return p;
}

/** Merge stored permissions onto the role template, dropping anything unknown. */
export function normalizePermissions(existing: unknown, role: Role): Permissions {
  const base = roleTemplate(role);
  if (!existing || typeof existing !== "object") return base;
  const e = existing as Partial<Permissions> & Record<string, any>;
  for (const id of Object.keys(base.pages)) {
    const v = e.pages?.[id];
    if ((ACCESS_LEVELS as readonly string[]).includes(v as string)) base.pages[id] = v as AccessLevel;
  }
  for (const id of Object.keys(base.actions)) {
    if (typeof e.actions?.[id] === "boolean") base.actions[id] = e.actions[id];
  }
  for (const [page, defs] of Object.entries(base.columns)) {
    for (const key of Object.keys(defs)) {
      const old = e.columns?.[page]?.[key];
      if (old && typeof old === "object") {
        if (typeof old.view === "boolean") defs[key].view = old.view;
        if (typeof old.edit === "boolean") defs[key].edit = old.edit;
      }
    }
  }
  const s = e.dataScope;
  if (s && typeof s === "object") {
    if ((DATA_SCOPES as readonly string[]).includes(s.type)) base.dataScope.type = s.type;
    base.dataScope.supplierUid = String(s.supplierUid || "");
    base.dataScope.agentUid = String(s.agentUid || "");
    base.dataScope.poUids = Array.isArray(s.poUids) ? s.poUids.map(String) : [];
  }
  // A QC inspector never gets pages or actions (only Ready for QC, their inspections and Help).
  if (role === "QC inspector") {
    for (const id of Object.keys(base.pages)) base.pages[id] = "none";
    for (const id of Object.keys(base.actions)) base.actions[id] = false;
  }
  // Only Admins may ever reach the User Database.
  if (role !== "Admin") {
    base.pages.operatordb = "none";
    base.actions.manageUsers = false;
    base.actions.managePermissions = false;
  }
  return base;
}

/** A QC inspector (not Falcon support): only Ready for QC, their own inspections and Help. */
export const isQcInspector = (actor: { role: string; supportAccess?: boolean } | null | undefined) => !!actor && !actor.supportAccess && actor.role === "QC inspector";

/** Who is asking: their role in the organisation (a label), their access (what they may open and do) and the detailed
 *  permissions worked out from it. */
export interface Actor { role: Role; permissions: Permissions; supportAccess?: boolean; access?: Access }

export function pageAccess(actor: Actor | null, pageId: string): AccessLevel {
  if (!actor) return "none";
  if (actor.supportAccess) return "full";
  if (actor.role === "QC inspector") return "none";
  // With access set per user, the detailed pages are worked out from it (Settings and the User Database: Full on Settings).
  if (!actor.access) {
    if (pageId === "settings") return "view";
    if (pageId === "operatordb" && actor.role !== "Admin") return "none";
  }
  if (pageId === "activity" && !actor.permissions.actions.viewActivity) return "none";
  const v = actor.permissions.pages[pageId];
  return (ACCESS_LEVELS as readonly string[]).includes(v) ? v : "none";
}

const LEVEL_RANK: Record<AccessLevel, number> = { none: 0, view: 1, edit: 2, full: 3 };
export function hasPageAccess(actor: Actor | null, pageId: string, needed: AccessLevel = "view"): boolean {
  return LEVEL_RANK[pageAccess(actor, pageId)] >= LEVEL_RANK[needed];
}

export function canAction(actor: Actor | null, action: string): boolean {
  if (!actor) return false;
  if (actor.supportAccess) return true;
  if ((action === "manageUsers" || action === "managePermissions") && !actor.access && actor.role !== "Admin") return false;
  return !!actor.permissions.actions[action];
}

export function canViewColumn(actor: Actor | null, pageId: string, key: string): boolean {
  if (!actor) return false;
  if (actor.supportAccess) return true;
  const x = actor.permissions.columns[pageId]?.[key];
  return x ? x.view !== false : true;
}

export function canEditColumn(actor: Actor | null, pageId: string, key: string): boolean {
  if (!actor) return false;
  if (actor.supportAccess) return true;
  const x = actor.permissions.columns[pageId]?.[key];
  return !!x?.view && !!x?.edit && hasPageAccess(actor, pageId, "edit");
}

/** Remove the columns a user may not see before a record leaves the server. */
export function stripHiddenColumns<T extends Record<string, unknown>>(actor: Actor | null, pageId: string, row: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(row)) if (canViewColumn(actor, pageId, k)) (out as any)[k] = v;
  return out;
}

/** Keep only the edits a user is allowed to make; report the ones refused. */
export function allowedEdits(actor: Actor | null, pageId: string, changes: Record<string, unknown>): { allowed: Record<string, unknown>; refused: string[] } {
  const allowed: Record<string, unknown> = {};
  const refused: string[] = [];
  const known = new Set((COLUMN_DEFS[pageId] || []).map(([k]) => k));
  for (const [k, v] of Object.entries(changes)) {
    if (!known.has(k) ? hasPageAccess(actor, pageId, "edit") : canEditColumn(actor, pageId, k)) allowed[k] = v;
    else refused.push(k);
  }
  return { allowed, refused };
}

export function scopeLabel(role: Role, p: Permissions): string {
  if (role === "Admin" && p.dataScope.type === "all") return "All Records";
  return ({ all: "All Records", supplier: "Linked Supplier Only", agent: "Linked Agent Only", selectedPOs: "Selected POs Only" } as const)[p.dataScope.type];
}

/** Admins, and the Falcon platform owner, must use two-factor login. */
export function roleRequiresTwoFactor(role: Role): boolean {
  return role === "Admin";
}
